/**
 * Byte offsets at which a new recorder container starts inside joined audio: a
 * WebM/EBML header (1A 45 DF A3) or an MP4 `ftyp` box. Each resume of a draft
 * starts a new recorder, so a recording is several containers back to back,
 * and a player stops at the end of the first unless it is split here.
 */
export function findContainerStarts(bytes: Uint8Array): number[] {
  const starts: number[] = [];
  for (let i = 0; i + 8 <= bytes.length; i++) {
    const isEbml =
      bytes[i] === 0x1a &&
      bytes[i + 1] === 0x45 &&
      bytes[i + 2] === 0xdf &&
      bytes[i + 3] === 0xa3;
    const isFtyp =
      bytes[i + 4] === 0x66 &&
      bytes[i + 5] === 0x74 &&
      bytes[i + 6] === 0x79 &&
      bytes[i + 7] === 0x70;
    if (isEbml || isFtyp) starts.push(i);
  }
  return starts;
}

/** Where one recorder segment starts in the joined audio. */
export interface SegmentStart {
  byteOffset: number;
  startSeconds: number;
}

/**
 * Byte offset and start time of each recorder segment in chunks joined in
 * index order. `segmentStarts` holds the chunk index each segment began at,
 * and `segmentStartOffsets` its start time, by position. The first segment
 * always starts at byte 0.
 */
export function segmentStartsInJoinedChunks(
  chunks: { index: number; size: number }[],
  segmentStarts: number[],
  segmentStartOffsets: number[],
): SegmentStart[] {
  const segments: SegmentStart[] = [];
  let byteOffset = 0;
  for (const chunk of chunks) {
    const at = segmentStarts.indexOf(chunk.index);
    if (at >= 0) {
      segments.push({ byteOffset, startSeconds: segmentStartOffsets[at] ?? 0 });
    }
    byteOffset += chunk.size;
  }
  if (!segments.length || segments[0].byteOffset !== 0) {
    segments.unshift({ byteOffset: 0, startSeconds: 0 });
  }
  return segments;
}
